import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { Button, Spinner } from '@tutero/ui';

import {
  signInWithEmail,
  toAuthError,
  type SecondFactorChallenge,
} from '@/lib/auth';
import { env } from '@/lib/env';

import { useRefreshSession } from '../session';
import { AuthErrorText, AuthField } from './AuthField';
import { MfaStep } from './MfaStep';

/**
 * Sign in, on the admin's own origin (`staff-sign-in-here` D1 — which reverses
 * `admin-is-an-app` D1/D3's redirect to the teacher's app). The one auth Worker in
 * MathGaps/schools is still the only auth (A1): this form POSTs its routes from this
 * origin, which `AUTH_APP_ORIGINS` names, and holds no session of its own.
 *
 * It navigates nothing (D9). The shell already decides what a session sees — `isStaff`
 * first, then the nav the grants open — so a form that also routed would race it; a
 * success asks for the session again and the shell takes it from there.
 *
 * Request Access is a door again, as Flutter's footer has it (`login_form.dart:316-332`).
 * `request-access-is-a-page` D1 contradicts D8 here — the ruling that staff accounts are
 * made by an operator — and says so under a DISCUSS-FIRST note the operator has still to
 * answer. It no longer leads nowhere: the ask reaches the auth Worker, and an approver
 * answers it on the Permissions page's Requests tab (MathGaps/schools #1445).
 */
export function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<SecondFactorChallenge | null>(
    null,
  );
  const refreshSession = useRefreshSession();
  const titleId = useId();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithEmail(email.trim().toLowerCase(), password);
      refreshSession();
    } catch (err) {
      const failure = toAuthError(err);
      /* The password was right and a factor is owed: not a refusal, a step (D4). */
      if (failure.code === 'multi-factor-required' && failure.challenge) {
        setChallenge(failure.challenge);
      } else {
        setError(failure.message);
      }
    } finally {
      /* Also on success: a session read that still answers `null` must leave a live form. */
      setBusy(false);
    }
  }

  if (challenge) {
    return <MfaStep challenge={challenge} onVerified={refreshSession} />;
  }

  return (
    /* 600px and a bigger title than a page's, as Flutter's sign-in is (`login_screen.dart`). */
    <form
      className="flex w-full max-w-[600px] flex-col gap-4"
      aria-labelledby={titleId}
      onSubmit={(e) => void submit(e)}
    >
      <h1 id={titleId} className="text-headline-2 mb-2">
        Tutero Admin
      </h1>
      {/* `type` and `required` are the whole of the validation: Flutter's validators never block submit (D5, C2). */}
      <AuthField
        label="Email"
        type="email"
        autoComplete="email"
        placeholder="Enter your email"
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <AuthField
        label="Password"
        password
        autoComplete="current-password"
        placeholder="Enter your password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      <AuthErrorText>{error}</AuthErrorText>
      {/* Disabled while the call is in flight: Flutter's button is not, so a double click signs in twice (C4). */}
      <Button type="submit" disabled={busy} aria-busy={busy}>
        {busy ? <Spinner /> : null}
        Log In
      </Button>
      {/* Flutter's order: the way in before the way back in (`login_form.dart:316-344`). */}
      <p className="text-regular-s">
        Don’t have an account?{' '}
        <Link to="/admin-access" className="text-primary underline">
          Request Access
        </Link>
      </p>
      {/* The teacher's app owns the reset email and the page its link lands on (D8). */}
      <p className="text-regular-s">
        Forgot your password?{' '}
        <a
          href={`${env.VITE_WEB_ORIGIN}/forgot-password`}
          className="text-primary underline"
        >
          Reset Password
        </a>
      </p>
    </form>
  );
}
